import {
  all,
  delay,
  fork,
  put,
  race,
  retry,
  select,
  takeEvery,
} from 'redux-saga/effects';
import { captureException } from '@sentry/nextjs';
import ActionTypes from '../../../../Data/ActionTypes';
import SagaStatus from '../../../../Data/SagaStatus';
import getCustomerMatchesQuery from './getCustomerMatchesQuery';
import MatchingSupplierCountSource from '../../../../Data/MatchingSupplierCountSource';
import {
  convertPartialSubmissionToInputAnswers,
  getMaxMatchesToDisplay,
  isMatchImpliedByPrivacyStatement,
  setSupplierMatchOptionsAndMarkSelected,
  shouldLegalStatementBeAboveNavigation,
  SUBCATEGORY_MAX_MATCHES_DEFAULT,
} from '../../../../Helpers/supplierMatches';
import { getPartialSubmission, getUrl } from '../../../../Helpers/submission';
import {
  exposeBrandMatchReasonsEvent,
  exposeMatchPoolUpdatedEvent,
  exposeScoreSummaryEvent,
  exposeSupplierMatchesRequestedEvent,
} from '../../../../Helpers/events';
import { getMatchReasonsWorker } from '../getMatchReasons/getMatchReasons';
import buildBrandMatchReasons from './buildBrandMatchReasons';
import { getCampaignIdTrumpedByUrl } from '../../../../Helpers/campaignId';
import { NAVIGATION_BLOCKS } from '../../../../Data/Constants';
import { incrementMetric } from '../../../../Services/Datadog';
import { buildDynamicMatches } from '../../../../Helpers/dynamicSelectAndConnect';
import { matchLevels } from './matchLevels';

export const MAX_TRIES = 3;
const QUERY_RETRY_DELAY_MS = 1000;
const MAX_REQUEST_DURATION_MS = 30000;

const REQUIRED_VARIABLES = [
  'subcategoryId',
  'countryCode',
  'visitorId',
  'answers',
];
const NO_CUSTOMER_MATCHES_ERROR =
  'Get customer matches request returned no matches';
export const GET_CUSTOMER_MATCHES_REQUEST_FAILED =
  'Get customer request failed';

const sendCustomerMatchMetric = (formConfig, matchType) => {
  incrementMetric({
    name: 'customerMatch',
    tags: {
      match_type: matchType,
      form_id: formConfig.formId,
      subcategory_id: formConfig.subcategoryId,
    },
  });
};

/**
 * Stores brandMatchReasons built from the enriched matches and exposes them to
 * the formLoader/results page, mirroring the separate getMatchReasons flow.
 */
function* publishBrandMatchReasons({ customerMatches, iFrameId }) {
  const brandMatchReasons = buildBrandMatchReasons(customerMatches);
  if (brandMatchReasons.length === 0) return;

  try {
    yield put({
      type: 'SUPPLIER_MATCHES/setBrandMatchReasons',
      payload: { brandMatchReasons },
    });

    exposeBrandMatchReasonsEvent({ iFrameId, brandMatchReasons });
  } catch (error) {
    // A cosmetic reasons failure must not downgrade a successful match result.
    // The results page will fall back to whatOthersLike content.
    captureException(error);
  }
}

function* setAllCustomerMatchNavigationBlocks({
  isPreventingNavigation,
  pagesBlockedDuringCustomerMatchesRequest,
}) {
  yield all(
    pagesBlockedDuringCustomerMatchesRequest.map((pageNumber) =>
      put({
        type: 'NAVIGATION_CONTROLS/setNavigationBlockOnPage',
        payload: {
          pageNumber,
          blockName: NAVIGATION_BLOCKS.CUSTOMER_MATCHES,
          isPreventingNavigation,
        },
      })
    )
  );
}

function* setCustomerMatchesRequestStatus(customerMatchesRequestStatus) {
  yield put({
    type: 'SUPPLIER_MATCHES/setCustomerMatchesRequestStatus',
    payload: { customerMatchesRequestStatus },
  });
}

function* requestCustomerMatches() {
  const {
    components,
    supplierMatches,
    submission,
    formConfig,
    userIdentities,
    optionalFormConfig,
    features,
  } = yield select();
  const { dynamicSelectAndConnect } = features || {};
  const { iFrameId } = optionalFormConfig;
  // The consolidated AI-enriched path (GOLD-2328).
  const isPolarisForm = Boolean(formConfig?.isPolarisForm);
  const DEFAULT_HIDE_ON_SINGLE_MATCH = false;

  // Get all Supplier match components.
  const supplierMatchComponents = Object.values(components).filter(
    (component) => component.component.type === 'supplier-match'
  );

  const shouldGetMatches =
    (supplierMatchComponents.length > 0 &&
      supplierMatches.supplierMatchSelectionEnabled) ||
    supplierMatches?.showCustomerMatchesOnThankYouPage;

  if (!shouldGetMatches) {
    return;
  }

  yield* setCustomerMatchesRequestStatus(SagaStatus.PENDING);

  const { pagesBlockedDuringCustomerMatchesRequest } = supplierMatches;
  // When AI matching is on, only the supplier match page itself needs to wait
  // for the matches result. The intermediate pages (e.g. a loader and the
  // following data-collection steps) are released so the user flows through them
  // while the request is in flight; the request typically resolves before they
  // reach the supplier match page.
  const supplierMatchPages = supplierMatchComponents.map(
    (component) => component.component.page
  );
  const pagesToBlockDuringRequest = isPolarisForm
    ? pagesBlockedDuringCustomerMatchesRequest.filter((pageNumber) =>
        supplierMatchPages.includes(pageNumber)
      )
    : pagesBlockedDuringCustomerMatchesRequest;
  let customerMatches;
  let customerMatchLevel;
  let scoreSummary = null;

  // Default in case there is no value on the component and the customer match request errors
  let subcategoryMaxMatches = SUBCATEGORY_MAX_MATCHES_DEFAULT;

  // Workaround: In the future max matches or SPL should be obtained on a per subcategory basis from the request. For now we use a value on the component
  const maxMatches =
    supplierMatchComponents[0]?.component?.maxMatches ||
    SUBCATEGORY_MAX_MATCHES_DEFAULT;

  const hideOnSingleMatch =
    supplierMatchComponents[0]?.component?.hideOnSingleMatch ||
    DEFAULT_HIDE_ON_SINGLE_MATCH;

  yield setAllCustomerMatchNavigationBlocks({
    isPreventingNavigation: true,
    pagesBlockedDuringCustomerMatchesRequest: pagesToBlockDuringRequest,
  });

  const campaignId =
    getCampaignIdTrumpedByUrl(
      formConfig.campaignId,
      getUrl(formConfig.pageUrl),
      optionalFormConfig.useCidFromURL
    ) ?? undefined;

  let hasDispatchedLoadingStart = false;
  try {
    yield put({
      type: 'SUPPLIER_MATCHES/requestMatchingSupplierCountLoading',
      payload: {
        isLoading: true,
        matchingSupplierCountLoadingSource: MatchingSupplierCountSource.MATCHES,
      },
    });
    hasDispatchedLoadingStart = true;

    // Get a list of supplier matches for the customer generated dynamically based on customer data.
    const answers = convertPartialSubmissionToInputAnswers(
      getPartialSubmission(components),
      supplierMatchComponents,
      supplierMatches.subcategoryMatches
    );

    const args = {
      variables: {
        subcategoryId: submission.webform.subcategoryId,
        countryCode: formConfig.countryCode,
        campaignId,
        visitorId: userIdentities.visitorId,
        answers,
        predefinedSupplierId: formConfig?.supplierId,
        formId: String(formConfig.formId),
        // Registry USPs feed Select and Connect cards and the
        // thankYouPageRequested event on results-page journeys. The redirect to
        // a results page is decided by the parent page after that event fires,
        // so nothing here can tell which journeys need them; every journey asks.
        withUsps: true,
        testMode: optionalFormConfig.testMode,
      },
    };

    if (dynamicSelectAndConnect?.enableDynamicSelectAndConnect) {
      const subcategoryFallbackBrands =
        supplierMatches.subcategoryMatches || [];
      args.variables.fallbackSupplierIds = subcategoryFallbackBrands
        .map((b) => b.supplierId)
        .filter(Boolean);
      args.variables.mode = 'ALL_EXCLUSIVES'; // all dynamic select and connect brands are exclusive suppliers, this mode tracks through to the request to leads
    }

    if (isPolarisForm) {
      args.variables.withAiEnrichment = true;
      args.variables.subcategoryName = formConfig.subcategoryName;
    }

    const missingVariables = REQUIRED_VARIABLES.filter(
      (variable) => !args.variables[variable]
    );

    if (missingVariables.length > 0) {
      const missingVariablesString = missingVariables.join(', ');
      throw new Error(
        `Required variables (${missingVariablesString}) for the supplier match request are missing.`
      );
    }

    try {
      const { result, timeout } = yield race({
        result: retry(
          MAX_TRIES,
          QUERY_RETRY_DELAY_MS,
          getCustomerMatchesQuery,
          args
        ),
        timeout: delay(MAX_REQUEST_DURATION_MS), // 30 seconds timeout
      });

      if (timeout) {
        throw new Error('Request timed out after 30 seconds');
      }

      if (!timeout) {
        customerMatches = result.customerMatches;
        customerMatchLevel = result.matchLevel;
        subcategoryMaxMatches = result.maximumMatches;
        scoreSummary = result.scoreSummary ?? null;
      }
    } catch (error) {
      throw new Error(GET_CUSTOMER_MATCHES_REQUEST_FAILED);
    }

    if (customerMatches.length === 0) {
      throw new Error(NO_CUSTOMER_MATCHES_ERROR);
    }

    if (isPolarisForm) {
      // The gateway returns the full eligible set already ordered; this is the
      // single truncation applied before the list is exposed.
      customerMatches = customerMatches.slice(
        0,
        getMaxMatchesToDisplay({
          subcategoryMaxMatches,
          componentMaxMatches: maxMatches,
        })
      );
    }

    yield put({
      type: 'SUPPLIER_MATCHES/setCustomerMatches',
      payload: {
        customerMatches,
      },
    });

    yield put({
      type: 'SUPPLIER_MATCHES/setSubcategoryMaxMatches',
      payload: {
        subcategoryMaxMatches,
      },
    });

    let dynamicMatches = [];
    if (
      dynamicSelectAndConnect?.enableDynamicSelectAndConnect &&
      dynamicSelectAndConnect.maxBrands
    ) {
      const subcategoryFallbackBrands =
        supplierMatches.subcategoryMatches || [];
      const enrichedFallbacks = subcategoryFallbackBrands.filter(
        (b) => b.supplierId
      );
      dynamicMatches = buildDynamicMatches({
        customerMatches,
        fallbacks: enrichedFallbacks,
        targetCount: dynamicSelectAndConnect.maxBrands,
      });

      yield put({
        type: 'SUPPLIER_MATCHES/setDynamicMatches',
        payload: { dynamicMatches },
      });
    }

    // In dynamic Select and Connect the primary match is the only brand
    // submitted on form submit; secondary matches are offered on the Select
    // and Connect page. The primary comes from dynamicMatches, which is
    // already exclusive-filtered, so we never fall back to the raw
    // customerMatches here (those may include non-exclusive brands).
    const supplierMatchMatches =
      dynamicSelectAndConnect?.enableDynamicSelectAndConnect
        ? dynamicMatches.slice(0, 1)
        : customerMatches;

    const maxMatchesToDisplay = getMaxMatchesToDisplay({
      subcategoryMaxMatches,
      componentMaxMatches: maxMatches,
    });

    yield put({
      type: 'SUPPLIER_MATCHES/setRequiresLegalStatementAboveNav',
      payload: {
        requiresLegalStatementAboveNav: shouldLegalStatementBeAboveNavigation(
          customerMatches,
          maxMatchesToDisplay
        ),
      },
    });

    // It is unlikely we will need multiple supplier match components but in the event that this is necessary (conditionals etc) then loop through all supplier match components setting their options.
    yield all(
      supplierMatchComponents.map((supplierMatchComponent) =>
        setSupplierMatchOptionsAndMarkSelected(
          supplierMatchComponent,
          supplierMatchMatches,
          subcategoryMaxMatches
        )
      )
    );

    const liveMatchesCount = Math.min(
      customerMatches.length,
      subcategoryMaxMatches
    );

    yield put({
      type: 'SUPPLIER_MATCHES/setMatchingSupplierCount',
      payload: {
        matchingSupplierCount: liveMatchesCount,
        matchingSupplierCountSource: MatchingSupplierCountSource.MATCHES,
      },
    });
    exposeMatchPoolUpdatedEvent({
      source: 'supplierMatch',
      count: liveMatchesCount,
      iFrameId,
    });

    if (!isPolarisForm) {
      yield fork(getMatchReasonsWorker, customerMatches);
    } else {
      yield* publishBrandMatchReasons({ customerMatches, iFrameId });
    }
  } catch (error) {
    // Fallback matches have been provided from Capture in the form of the highest revenue brands for this subcategory. Set these as component options and mark as selected.
    yield all(
      supplierMatchComponents.map((supplierMatchComponent) =>
        setSupplierMatchOptionsAndMarkSelected(
          supplierMatchComponent,
          supplierMatches.subcategoryMatches,
          subcategoryMaxMatches
        )
      )
    );

    yield put({
      type: 'SUPPLIER_MATCHES/setCustomerMatches',
      payload: {
        customerMatches: [],
      },
    });

    yield put({
      type: 'SUPPLIER_MATCHES/setSubcategoryMaxMatches',
      payload: {
        subcategoryMaxMatches,
      },
    });

    const fallbackMatchesCount = Math.min(
      supplierMatches.subcategoryMatches.length,
      subcategoryMaxMatches
    );

    yield put({
      type: 'SUPPLIER_MATCHES/setMatchingSupplierCount',
      payload: {
        matchingSupplierCount: fallbackMatchesCount,
        matchingSupplierCountSource: MatchingSupplierCountSource.FALLBACK,
      },
    });
    exposeMatchPoolUpdatedEvent({
      source: 'supplierMatch',
      count: fallbackMatchesCount,
      iFrameId,
    });

    const maxMatchesToDisplay = getMaxMatchesToDisplay({
      subcategoryMaxMatches,
      componentMaxMatches: maxMatches,
    });

    yield put({
      type: 'SUPPLIER_MATCHES/setRequiresLegalStatementAboveNav',
      payload: {
        requiresLegalStatementAboveNav: shouldLegalStatementBeAboveNavigation(
          supplierMatches.subcategoryMatches,
          maxMatchesToDisplay
        ),
      },
    });

    yield setAllCustomerMatchNavigationBlocks({
      isPreventingNavigation: false,
      pagesBlockedDuringCustomerMatchesRequest: pagesToBlockDuringRequest,
    });

    exposeSupplierMatchesRequestedEvent({
      eventName: 'customerMatchesRequested',
      iFrameId,
      isSuccess: false,
      maxMatches: maxMatchesToDisplay.toString(),
      isMatchImpliedByPrivacyStatement: isMatchImpliedByPrivacyStatement(
        hideOnSingleMatch,
        maxMatchesToDisplay.toString(),
        supplierMatches.subcategoryMatches,
        supplierMatches.supplierMatchSelectionEnabled
      ),
    });

    sendCustomerMatchMetric(
      formConfig,
      matchLevels.SUBCATEGORY_REVENUE_FALLBACK
    );

    yield put({
      type: 'SUPPLIER_MATCHES/setMatchLevel',
      payload: {
        matchLevel: matchLevels.SUBCATEGORY_REVENUE_FALLBACK,
      },
    });

    if (
      dynamicSelectAndConnect?.enableDynamicSelectAndConnect &&
      dynamicSelectAndConnect.maxBrands
    ) {
      const subcategoryFallbackBrands =
        supplierMatches.subcategoryMatches || [];
      const enrichedFallbacks = subcategoryFallbackBrands.filter(
        (b) => b.supplierId
      );
      const dynamicMatches = buildDynamicMatches({
        customerMatches: [],
        fallbacks: enrichedFallbacks,
        targetCount: dynamicSelectAndConnect.maxBrands,
      });

      yield put({
        type: 'SUPPLIER_MATCHES/setDynamicMatches',
        payload: { dynamicMatches },
      });
    }

    yield* setCustomerMatchesRequestStatus(SagaStatus.COMPLETE);

    // In the event that customer specific supplier matches cannot be obtained form the query, fail silently and fallback to subcategory matches which have already been set.
    if (
      error.message === GET_CUSTOMER_MATCHES_REQUEST_FAILED ||
      error.message === NO_CUSTOMER_MATCHES_ERROR
    ) {
      const fallbackMatches = supplierMatches.subcategoryMatches.slice(
        0,
        maxMatchesToDisplay
      );

      // Fallback matches are not AI-scored, so requesting AI match reasoning
      // for them would reorder and filter the results page (GOLD-2418).
      // Publishing empty reasons immediately lets the results page skip the
      // reasoning wait and render the what-others-like copy instead.
      if (isPolarisForm) {
        yield* publishBrandMatchReasons({
          customerMatches: fallbackMatches,
          iFrameId,
        });
        return;
      }

      yield fork(getMatchReasonsWorker, fallbackMatches);
      return;
    }

    throw error;
  } finally {
    if (hasDispatchedLoadingStart) {
      yield put({
        type: 'SUPPLIER_MATCHES/requestMatchingSupplierCountLoading',
        payload: {
          isLoading: false,
          matchingSupplierCountLoadingSource: null,
        },
      });
    }
  }
  yield setAllCustomerMatchNavigationBlocks({
    isPreventingNavigation: false,
    pagesBlockedDuringCustomerMatchesRequest: pagesToBlockDuringRequest,
  });

  const maxMatchesToDisplay = getMaxMatchesToDisplay({
    subcategoryMaxMatches,
    componentMaxMatches: maxMatches,
  });

  exposeSupplierMatchesRequestedEvent({
    eventName: 'customerMatchesRequested',
    iFrameId,
    isSuccess: true,
    maxMatches: maxMatchesToDisplay.toString(),
    supplierMatches: customerMatches,
    customerMatchLevel,
    isMatchImpliedByPrivacyStatement: isMatchImpliedByPrivacyStatement(
      hideOnSingleMatch,
      maxMatchesToDisplay.toString(),
      customerMatches,
      supplierMatches.supplierMatchSelectionEnabled
    ),
  });

  // The gateway only populates scoreSummary when the AI cutoff ran; a null summary
  // means there is no cutoff decision to report, so nothing is relayed.
  if (scoreSummary !== null) {
    exposeScoreSummaryEvent({
      iFrameId,
      visitorId: userIdentities.visitorId,
      scoreSummary,
    });
  }

  sendCustomerMatchMetric(formConfig, customerMatchLevel);

  yield put({
    type: 'SUPPLIER_MATCHES/setMatchLevel',
    payload: {
      matchLevel: customerMatchLevel,
    },
  });

  yield* setCustomerMatchesRequestStatus(SagaStatus.COMPLETE);
}

// The BSA lead waits while the request is PENDING, so a request that throws
// before reaching COMPLETE must not leave it held forever.
function* completeRequestStillPending() {
  const { supplierMatches } = yield select();
  if (supplierMatches?.customerMatchesRequestStatus === SagaStatus.PENDING) {
    yield* setCustomerMatchesRequestStatus(SagaStatus.COMPLETE);
  }
}

/**
 * Retrieves supplier matches for a customer and updates supplier match component options accordingly.
 *
 * @generator
 * @function getCustomerMatchesWorker
 * @yields {object} - Redux action objects dispatched to update customer matches and set supplier matches.
 */
export function* getCustomerMatchesWorker() {
  try {
    yield* requestCustomerMatches();
  } finally {
    yield* completeRequestStillPending();
  }
}

export default function* getCustomerMatchesWatcher() {
  yield takeEvery(ActionTypes.GET_CUSTOMER_MATCHES, getCustomerMatchesWorker);
}
